import { MultiList, MultiRange, ReactiveComponent, ReactiveList, SingleList, SingleRange } from "@usereactify/search-internals";
import { ReactifySearchProviderProps, SENSOR_IDS } from "../components";
import { Config, ConfigFilter, ConfigFilterFacet } from "../types";
import { getCuration } from "./curation";
import { generateSortQuery, generateSortSort, getSortOption } from "./sortOption";
import { getFilterStack } from "./filters";
import { replaceDocCountWithParent } from "../hooks";

import pkg from "../../package.json";

export type GeneratePropsFilterGroupOptions = {
  provider: ReactifySearchProviderProps;
  config: Config;
  filterGroup?: ConfigFilter;
  query?: Record<string, string>;
};

export type GeneratePropsSensorOptions = {
  provider: ReactifySearchProviderProps;
  config: Config;
  query?: Record<string, string>;
};

export type GeneratePropsBase = {
  provider: ReactifySearchProviderProps;
  config: Config;
  query?: Record<string, string>;
};

export const getPropsFilterGroup = (options: GeneratePropsFilterGroupOptions) => {
  return [
    ...options.filterGroup?.options.map((filterGroupOption) => getPropsFilterGroupOption(options, filterGroupOption)) ?? [],
    ...getPropsSensors(options),
    getPropsResults(options),
  ];
};

export const getPropsFilterGroupOption = (options: GeneratePropsFilterGroupOptions, filterGroupOption: ConfigFilterFacet) => {
  let filterProps: Record<string, unknown> = {};

  if (filterGroupOption.displayType === "slider") {
    filterProps = getPropsFilterSlider(filterGroupOption);
  }

  if (filterGroupOption.displayType === "single" || filterGroupOption.displayType === "multi") {
    if (filterGroupOption.displayView === "range") {
      filterProps = getPropsFilterRange(filterGroupOption);
    } else {
      filterProps = getPropsFilterList(filterGroupOption);
    }
  }

  const sharedProps = getPropsShared(options, filterGroupOption);

  return {
    ...sharedProps,
    ...filterProps,
  };
};

export const getPropsSensors = (options: GeneratePropsSensorOptions) => {
  return [
    ...getPropsSensorCustom(options),
    getPropsSensorCollection(options),
    getPropsSensorPublished(options),
    getPropsSensorInventoryAvailable(options),
    getPropsSensorSort(options),
  ].filter(Boolean);
}

export const getPropsSensorCustom = (options: GeneratePropsSensorOptions) => {
  const sensors = options.provider.mode === "collection" ? options.config.sensors.collection : options.config.sensors.search;

  return sensors.map((sensor, index) => ({
    componentId: `SensorCustom_${index}`,
    customQuery: () => sensor,
    source: ReactiveComponent,
  }));
}

export const getPropsSensorCollection = (options: GeneratePropsSensorOptions) => {
  if (options.provider.mode !== "collection") {
    return null;
  }
  
  return {
    componentId: "SensorCollection",
    customQuery: () => {
      if (options.provider.mode !== "collection") {
        return null;
      }

      const conditions: Array<unknown> = [];

      if (options.provider.market) {
        conditions.push({
          nested: {
            path: "curations",
            query: {
              bool: {
                should: [
                  {
                    nested: {
                      path: "collections",
                      query: {
                        term: {
                          "collections.handle.keyword": options.provider.collectionHandle,
                        },
                      },
                    },
                  },
                  {
                    nested: {
                      path: "curations",
                      query: {
                        term: {
                          "curations.collectionHandle.keyword":
                            options.provider.collectionHandle,
                        },
                      },
                    },
                  },
                ],
                must: [{
                  nested: {
                    path: "curations",
                    query: {
                      term: {
                        "curations.markets.keyword": options.provider.market,
                      },
                    },
                  },
                }]
              },
            },
          },
        });
      }
      
      return {
        query: {
          bool: {
            should: [
              {
                nested: {
                  path: "collections",
                  query: {
                    term: {
                      "collections.handle.keyword": options.provider.collectionHandle,
                    },
                  },
                },
              },
              {
                nested: {
                  path: "curations",
                  query: {
                    term: {
                      "curations.collectionHandle.keyword":
                        options.provider.collectionHandle,
                    },
                  },
                },
              },
            ],
          },
        },
      } 
    },
    source: ReactiveComponent,
  };
}

export const getPropsSensorPublished = (options: GeneratePropsSensorOptions) => {
  return {
    componentId: "SensorPublished",
    customQuery: () => {
      if (options.provider.market) {
        return {
          query: {
            bool: {
              must:[{
                match: {
                  markets: options.provider.market,
                },
              },{
                match: {
                  published: true,
                },
              }]
            },
          },
        };
      }

      return {
        query: {
          match: {
            published: true,
          },
        },
      }
    },
    source: ReactiveComponent,
  }
}

export const getPropsSensorInventoryAvailable = (options: GeneratePropsSensorOptions) => {
  const filterStack = getFilterStack(options);

  if (!filterStack || "show_all" === filterStack.inventoryVisibility) {
    return null;
  }

  return {
    componentId: "SensorInventoryAvailable",
    customQuery: () => ({
      query: {
        bool: {
          should: [
            {
              bool: {
                must: [
                  {
                    term: {
                      type: {
                        value: "product",
                      },
                    },
                  },
                  {
                    nested: {
                      path: "variants",
                      query: {
                        match: { "variants.available": true },
                      },
                    },
                  },
                ],
              },
            },
            ...(options.provider.mode === "instant-search"
              ? []
              : [
                  {
                    term: {
                      type: {
                        value: "callout",
                      },
                    },
                  },
                ]),
          ],
          minimum_should_match: "1",
        },
      },
    }),
    source: ReactiveComponent,
  }
}

export const getPropsSensorSort = (options: GeneratePropsSensorOptions & { searchTerm?: string; sortOption?: string; }) => {
  const curation = getCuration(options);
  const sortOption = getSortOption(options);

  const sort = generateSortSort({
    provider: options.provider,
    config: options.config,
    curation: curation,
    sortOption: sortOption,
  });
  const query = generateSortQuery({
    sortOption: sortOption,
    curation: curation,
  });

  return {
    componentId: "SensorSort",
    customQuery: () => ({
      sort,
      query,
    }),
    source: ReactiveComponent,
  }
}

export const getPropsBase = (options: GeneratePropsBase) => {
  const props =  {
    app: options.config.index,
    url: options.config.endpoint,
    credentials: options.provider.credentials,
    theme: options.provider.theme,
    initialState: options.provider.preload?.state,
    transformSort: () => {
      const sortSensor = getPropsSensorSort(options);
      const customQuery = sortSensor.customQuery();

      return customQuery.sort;
    },
    transformRequest: (request: any) => {
      if ((request.url as string).endsWith("?")) {
        request.url = request.url.substring(0, request.url.length - 1);
      }
      return request;
    },
    transformResponse: async (response: any) => {
      // support filtering inside nested aggregations by moving the inner aggregation
      // up one level such that reactivesearch understands it
      //
      // see useReactiveFilterListProps > defaultQuery to see how a filter is being applied
      // to variant aggregations to ensure that only in stock sizes are displayed as filter
      // options, this involves a nested filter, which requires this fix
      //
      // https://github.com/appbaseio/reactivesearch/issues/1530
      if (
        response?.aggregations?.reactivesearch_nested?.reactify_nested_outer
      ) {
        response.aggregations.reactivesearch_nested =
          replaceDocCountWithParent(
            response?.aggregations?.reactivesearch_nested
              ?.reactify_nested_outer
          );
      }

      return response;
    },
    headers: {
      "x-reactify-shop": options.provider.shopifyPermanentDomain,
      "x-reactify-mode": options.provider.mode,
      "x-reactify-client-id": options.provider.clientId,
      "x-reactify-client-version": pkg.version,
    },
  };

  return props;
}

export const getPropsResults = (options: GeneratePropsFilterGroupOptions & { scrollTarget?: string | Element | HTMLDocument; pageSize?: number; }) => {
  const includeFieldsProp = (() => {
    return [
      ...(options.provider.includeFields ?? []),
      "id",
      "title",
      "type",
      "handle",
      "image",
      "variants.id",
      "variants.presentment_prices",
      "callout",
    ];
  })();
  const excludeFieldsProp = (() => {
    return [...(options.provider.excludeFields ?? [])];
  })();

  return {
    size: options.pageSize ?? options.filterGroup?.pageSize ?? 20,
    URLParams: true,
    showLoader: false,
    dataField: "title",
    showResultStats: false,
    react: getPropsReact(options),
    includeFields: includeFieldsProp,
    excludeFields: excludeFieldsProp,
    scrollTarget: options.scrollTarget,
    componentId: "page", // this sets ?page= in the URL
    scrollOnChange: false, // @todo make this better, it's really janky when enabled
    pagination: options.filterGroup?.paginationType !== "infinite_scroll",
    infiniteScroll: options.filterGroup?.paginationType === "infinite_scroll",
    renderNoResults: () => null, // always use only render, otherwise both are shown
    source: ReactiveList,
    // defaultQuery: () => ({
    //   sort: ["_score"],
    //   query: {
    //     match_all: {}
    //   }
    // })
  }
}

export const getPropsReact = (options: GeneratePropsFilterGroupOptions, filterGroupOption?: ConfigFilterFacet) => {
  const sensorIds = SENSOR_IDS(options.provider.mode, options.config);
  const reactProp = (() => {
    if (filterGroupOption?.settingsShowEmptyValues) {
      return {
        and: sensorIds,
      };
    }

    return {
      and: [
        ...sensorIds,
        ...(options.provider.additionalComponentIds ?? []),
        ...(options.filterGroup?.options.map((filter) => filter.handle) ?? []),
      ].filter((handle) => handle !== filterGroupOption?.handle),
    };
  })();

  return reactProp;
};

export const getPropsShared = (options: GeneratePropsFilterGroupOptions, filterGroupOption: ConfigFilterFacet) => {
  const reactProp = getPropsReact(options, filterGroupOption);
  const nestedFieldProp = (() => {
    const nestedFields = ["variants"];
    const topField = filterGroupOption.field.split(".")[0];
    if (nestedFields.includes(topField)) {
      return topField;
    }
    return undefined;
  })();
  const customQueryProp = (() => {
    if (
      !filterGroupOption.settingsHideUnavailable ||
      !filterGroupOption.field.startsWith("variants.")
    )
      return undefined;

    return (value?: string | string[]) => {
      if (!value) return {};

      // reactivesearch sometimes returns string, sometimes string[]
      const valueArray = "string" === typeof value ? [value] : value;

      if (0 === valueArray.length) return {};

      return {
        query: {
          nested: {
            path: "variants",
            query: {
              bool: {
                must: [
                  {
                    match: {
                      "variants.available": "true",
                    },
                  },
                  {
                    terms: {
                      [filterGroupOption.field]: valueArray,
                    },
                  },
                ],
              },
            },
          },
        },
      };
    };
  })();
  const defaultQueryProp = (() => {
    if (!filterGroupOption.field.startsWith("variants.")) {
      return undefined;
    }

    // override the aggregation query to filter the results by the variants.available attribute
    return () => {
      return {
        aggs: {
          reactivesearch_nested: {
            nested: { path: "variants" },
            aggs: {
              reactify_nested_outer: {
                filter: {
                  bool: {
                    minimum_should_match: 1,
                    should: [
                      {
                        match: {
                          "variants.available": "true",
                        },
                      },
                      {
                        match: {
                          "variants.available": filterGroupOption.settingsHideUnavailable
                            ? "true"
                            : "false",
                        },
                      },
                    ],
                  },
                },
                aggs: {
                  [filterGroupOption.field]: {
                    terms: {
                      field: filterGroupOption.field,
                      size: 100,
                      order: { _count: "desc" },
                    },
                    aggs: {
                      parent_docs: { reverse_nested: {} },
                    },
                  },
                },
              },
            },
          },
        },
      };
    };
  })();
  const sourceProp = (() => {
    if (filterGroupOption.displayType === "slider") {
      return ReactiveComponent;
    }
    if (filterGroupOption.displayType === "single") {
      if (filterGroupOption.displayView === "range") {
        return SingleRange;
      }
      return SingleList;
    }
    if (filterGroupOption.displayType === "multi") {
      if (filterGroupOption.displayView === "range") {
        return MultiRange;
      }
      return MultiList;
    }
    
    return;
  })();

  return {
    URLParams: true,
    componentId: filterGroupOption.handle,
    dataField: filterGroupOption.field,
    filterLabel: filterGroupOption.name,
    nestedField: nestedFieldProp,
    customQuery: customQueryProp,
    defaultQuery: defaultQueryProp,
    source: sourceProp,
    react: reactProp,
  }
};

export const getPropsFilterList = (filterGroupOption: ConfigFilterFacet) => {
  return {
    size: parseInt(filterGroupOption.displaySize) || undefined,
    queryFormat: filterGroupOption.settingsFilterLogic ?? "or",
    showFilter: filterGroupOption.settingsShowFilter ?? true,
    showLoadMore: filterGroupOption.settingsShowMore ?? false,
    showSearch: filterGroupOption.settingsShowSearch ?? false,
    showRadio: false,
    showCheckbox: false,
  };
};

export const getPropsFilterRange = (filterGroupOption: ConfigFilterFacet) => {
  const data = filterGroupOption.displayRangeOptions?.map((option) => {
    const [label, start, end] = option.split(":");
    return {
      label: label,
      start: start ? parseInt(start) : 0,
      end: end ? parseInt(end) : Number.MAX_SAFE_INTEGER,
    };
  }) ?? [];

  return {
    queryFormat: filterGroupOption.settingsFilterLogic ?? "or",
    showFilter: filterGroupOption.settingsShowFilter ?? true,
    showRadio: false,
    showCheckbox: false,
    data: data,
  };
};

export const getPropsFilterSlider = (filterGroupOption: ConfigFilterFacet) => {
  return {}
};